import React, { useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import withModal from "../withModal";
import { hideModal } from "../../../actions/actionCreators";
import TreesWithSearch from "../../tree/TreesWithSearch";
import MaterialModal from "../MaterialModal";
import TextField from "@material-ui/core/TextField";
import FormControl from "@material-ui/core/FormControl";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import RadioGroup from "@material-ui/core/RadioGroup";
import Checkbox from "@material-ui/core/Checkbox";
import Radio from "@material-ui/core/Radio";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import { createTheme, ThemeProvider } from "@material-ui/core/styles";
import { IAppState } from "../../../types/stores/index";

const theme = createTheme({
    overrides: {
        MuiInput: {
            underline: {
                "&&&&:hover:after": {
                    borderBottom: "2px solid #406eb3",
                },
                "&&&&:after": {
                    borderBottom: "2px solid #406eb3",
                },
            },
        },
        MuiInputBase: {
            input: {
                fontSize: "15px",
            },
        },
        MuiFormHelperText: {
            root: {
                fontSize: "14px",
            },
        },
    },
});

const flowOfferCodeValueModalProps = {
    // Injected from Redux
    modalProps: PropTypes.shape({
        variableId: PropTypes.number.isRequired,
        variableKind: PropTypes.string.isRequired,
        variableName: PropTypes.string.isRequired,
        assignValues: PropTypes.func.isRequired,
        regex: PropTypes.string,
        dropdownValues: PropTypes.string,
    }).isRequired,
    isOpen: PropTypes.bool.isRequired,
    // Injected from Redux (ActionCreators)
    hideModal: PropTypes.func,
};

type FlowOfferCodeValueModalProps = PropTypes.InferProps<typeof flowOfferCodeValueModalProps>;

const FlowOfferCodeValueModal: React.FC<FlowOfferCodeValueModalProps> = ({
    modalProps,
    isOpen,
}: FlowOfferCodeValueModalProps) => {
    const dispatch = useDispatch();

    const [removedChecked, setRemovedChecked] = useState(false);
    const [showBTN, setShowBTN] = useState(false);
    const [value, setValue] = useState("");
    const [valueType, setValueType] = useState("text");
    const [sequencenum, setSequencenum] = useState(0);
    const [showSequence, setShowSequence] = useState(false);

    const selectedField = useSelector<IAppState, number>(state => state.selected.field);
    const enabledFeatures = useSelector((state: IAppState) => state.session.enabledFeatures || []);

    const id = modalProps && modalProps.variableId;
    const kind = modalProps && modalProps.variableKind;
    const variableName = modalProps && modalProps.variableName;
    const regex = modalProps && modalProps.regex;
    const dropdownValues = modalProps && modalProps.dropdownValues;
    const hide = () => {
        setValue("");
        setValueType("text");
        setShowBTN(false);
        setSequencenum(0);
        dispatch(hideModal("FLOW_OFFER_CODE_VALUE"));
    };

    const updateValues = () => {
        if (kind == "stringField") {
            modalProps.assignValues(id, kind, value + "+" + selectedField);
            hide();
        } else {
            if (kind == "field" && !selectedField) {
                return;
            }
            modalProps.assignValues(id, kind, kind == "string" ? value : selectedField);
            hide();
        }
    };

    const setOfferValueType = value => {
        setValueType(value);
        setValue("");
        setShowBTN(false);
    };

    const inputComponent = () => {
        if (dropdownValues) {
            const dropdownArr = dropdownValues.split("|");
            if (dropdownArr.length > 0) {
                return (
                    <>
                        <p>{"This will overwrite any previously assigned values to " + String(variableName) + "."}</p>
                        <Select
                            id="master-select"
                            value={value || ""}
                            disabled={valueType == "unique"}
                            onChange={e => {
                                setValue(e.target.value as string);
                                setShowBTN(true);
                            }}
                            onClick={() => {
                                if (value == "" && dropdownArr.filter(x => x == "").length > 0) {
                                    setShowBTN(true);
                                }
                            }}
                            style={{ fontSize: 16, width: "100%" }}
                        >
                            {dropdownArr.map(x => (
                                <MenuItem key={x} value={x}>
                                    {x}
                                </MenuItem>
                            ))}
                        </Select>
                    </>
                );
            }
        } else {
            let helperText = "This will overwrite any previously assigned values to " + String(variableName) + ".";
            let error = false;
            if (regex) {
                const reg = new RegExp(regex);
                if (!reg.test(value)) {
                    helperText = `Invalid value. Please ensure the value matches + ${regex}`;
                    error = true;
                }
            }
            return (
                <TextField
                    style={{ margin: "15px 0px" }}
                    type="text"
                    helperText={helperText}
                    value={value || ""}
                    disabled={valueType == "unique"}
                    error={error}
                    onChange={e => {
                        if (regex) {
                            const reg = new RegExp(regex);
                            setValue(e.target.value);
                            if (reg.test(e.target.value)) {
                                setShowBTN(true);
                            } else if (!reg.test(e.target.value)) {
                                setShowBTN(false);
                            }
                        } else if (e.target.value.match(/^$|^[a-z0-9_./\- ]+$/i)) {
                            setValue(e.target.value);
                            setShowBTN(true);
                        }
                    }}
                />
            );
        }
    };

    const setRemovedCheckedHandle = e => {
        setRemovedChecked(!removedChecked);
        let valueFormatted = "";
        if (removedChecked) {
            valueFormatted = value.replace(/}{/g, "}_{");
            setValue(valueFormatted);
        } else {
            valueFormatted = value.replace(/_/g, "");
            setValue(valueFormatted);
        }
        e.preventDefault();
    };

    const getPossibleValues = () => [
        "{FlowId}",
        "{FlowItemId}",
        "{FlowName}",
        "{date:yyyyMMddHHmmss}",
        "{date:yyMMdd}",
        "{date:yyyyMMdd}",
    ];

    const replaceUniqueIdentifier = template => {
        const searchTerm = "{sequencenumber:";
        const indexOfFirst = value.indexOf(searchTerm);
        const indexOfFirstNew = template.indexOf(searchTerm);
        if (indexOfFirst > -1 && indexOfFirstNew > -1) {
            const sequenceIndex = value.indexOf("}", indexOfFirst + 1);
            const replaced = value.substring(indexOfFirst, sequenceIndex + "}".length); // + sequenceNew + newValue.substring(index + 1);
            return value.replace(replaced, template);
        } else {
            return !removedChecked ? value + "_" + template : value + template;
        }
    };

    const addToUniqueIdentifier = template => {
        let newValue = "";
        if (enabledFeatures.includes("offer-remove-underscore")) {
            newValue = value != "" ? replaceUniqueIdentifier(template) : template;
        } else {
            newValue = value != "" && !removedChecked ? value + "_" + template : value + template;
        }

        updateUniqueIdentifierFormat(newValue);
    };

    const updateUniqueIdentifierFormat = newValue => {
        let pass = newValue.match(/^$|^[a-z0-9{}:#_\-. ]+$/i);

        if (newValue.includes(":")) {
            //Find out how : is being used
            let arr = newValue.split(":");
            let i = 0;
            for (i = 0; i < arr.length - 1; i++) {
                //-1 to ignore last item in a : split
                if (arr[i].substring(arr[i].length - 5, arr[i].length) != "{date") {
                    if (!arr[i].includes("{sequencenumber")) {
                        pass = false;
                    }
                }
            }
        }
        if (pass) {
            setValue(newValue);
            setShowBTN(true);
        } else {
            setShowBTN(false);
        }
    };

    const handleFilter = e => {
        setSequencenum(e.target.value);
        addToUniqueIdentifier("{sequencenumber:" + e.target.value + "}");
        setShowSequence(false);
    };

    const uniqueIdentifierFormat = () => (
        <>
            <br />
            {valueType == "unique" ? (
                <pre className="simple wrap">
                    {getPossibleValues().map((template, index) => (
                        <div key={index} className="badge clickable" onClick={() => addToUniqueIdentifier(template)}>
                            {template}
                        </div>
                    ))}
                    {showSequence ? (
                        <div key={5}>
                            <Select value={sequencenum} open={true} onChange={handleFilter} className="pull-right">
                                <MenuItem key={`${"sv"}empty`} disabled value={0}>
                                    Select sequence
                                </MenuItem>
                                {Array.from(
                                    Array(16),
                                    (e, i) =>
                                        i > 0 && (
                                            <MenuItem key={i} value={i}>
                                                {"{sequencenumber:"}
                                                {e}
                                                {i}
                                                {"}"}
                                            </MenuItem>
                                        )
                                )}
                            </Select>
                        </div>
                    ) : (
                        enabledFeatures.includes("offer-remove-underscore") && (
                            <div
                                key={`${"sv"}emptySt`}
                                className="badge clickable"
                                onClick={() => setShowSequence(true)}
                            >
                                {"{Sequence}"}
                            </div>
                        )
                    )}
                </pre>
            ) : null}
        </>
    );

    const modalFormat = () => {
        let formatString = <></>;
        let formatField = <></>;
        if (kind == "string") {
            formatString = (
                <ThemeProvider theme={theme}>
                    {!regex && !dropdownValues && (
                        <FormControl>
                            <RadioGroup
                                row
                                name="type"
                                value={valueType}
                                onChange={e => setOfferValueType(e.target.value)}
                            >
                                <div style={{ marginBottom: "10px" }}>
                                    <FormControlLabel value="text" control={<Radio color="primary" />} label="Text" />
                                    <FormControlLabel
                                        value="unique"
                                        control={<Radio color="primary" />}
                                        label="Unique Identifier"
                                    />
                                </div>
                            </RadioGroup>
                        </FormControl>
                    )}
                    {valueType == "unique" && enabledFeatures.includes("offer-remove-underscore") ? (
                        <div style={{ marginBottom: "10px" }}>
                            <FormControlLabel
                                control={<Checkbox color="primary" />}
                                style={{ transform: "scale(0.8)" }}
                                checked={removedChecked}
                                onClick={e => setRemovedCheckedHandle(e)}
                                label="Remove underscore"
                            />
                        </div>
                    ) : (
                        ""
                    )}
                    {inputComponent()}
                    {uniqueIdentifierFormat()}
                </ThemeProvider>
            );
        }
        if (kind == "stringField") {
            formatString = <ThemeProvider theme={theme}>{inputComponent()}</ThemeProvider>;
        }
        if (kind == "field" || kind == "stringField") {
            formatField = <TreesWithSearch showFieldTree outerHeight="66vh" />;
        }

        return (
            <>
                {formatString}
                {formatField}
            </>
        );
    };

    return (
        <MaterialModal
            title="Assign Offer Code Value"
            isOpen={isOpen}
            onClose={hide}
            id="flow-assign-offer"
            onPrimaryButtonRequest={updateValues}
            primaryButtonText="Ok"
            isPrimaryButtonDisabled={
                kind == "stringField"
                    ? !selectedField && showBTN == false
                    : (kind == "field" && !selectedField) || (kind == "string" && showBTN == false)
            }
        >
            {modalFormat()}
        </MaterialModal>
    );
};

export default withModal(FlowOfferCodeValueModal, "FLOW_OFFER_CODE_VALUE");
